import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '@env';
import {MembershipProduct, Success, MembershipInfo, AdditionalEventSub} from '@core/models';
import {first} from 'rxjs/operators';

declare const Stripe;

@Injectable({
  providedIn: 'root'
})
export class AccountService {

  constructor(
    private http: HttpClient,
  ) {
  }

  public redirectToCheckout(session): void {
    const stripe = Stripe(environment.stripeKey);
    stripe.redirectToCheckout({
      sessionId: session.sessionId,
    });
  }

  public changeTutorialStatus(status: { schedule_tutorial: boolean }): Observable<Success> {
    return this.http.patch<Success>(`${environment.apiUrl}users/tutorial`, status);
  }

  public getProducts(): Observable<MembershipProduct> {
    return this.http.get<MembershipProduct>(`${environment.apiUrl}subscriptions/user-subscription`);
  }

  public getMembershipMessage(): Observable<{ msg: string, user_type_id: number, status_id: number }> {
    return this.http.get<{ msg: string, user_type_id: number, status_id: number }>(`${environment.apiUrl}subscriptions/membership-message`);
  }

  public upgrade(priceId: string): Observable<MembershipProduct> {
    return this.http.post<MembershipProduct>(`${environment.apiUrl}subscriptions/upgrade`, {priceId});
  }

  public getMembershipInfo(): Observable<MembershipInfo> {
    return this.http.get<MembershipInfo>(`${environment.apiUrl}subscriptions/user-membership-info`);
  }

  public getMemberSession(priceId: string): void {
    this.http.post<{ sessionId: string }>(`${environment.apiUrl}subscriptions/create-checkout-session`, {
      priceId,
    }).subscribe((session: { sessionId: string }) => {
      this.redirectToCheckout(session);
    });
  }

  public getChangeCardSession(priceId: string): void {
    this.http.post<{ sessionId: string }>(`${environment.apiUrl}subscriptions/create-change-card-session`, {
      priceId,
    }).subscribe((session: { sessionId: string }) => {
      this.redirectToCheckout(session);
    });
  }

  // tslint:disable-next-line:variable-name
  public unsubscribe(subscription_id: string): Observable<Success> {
    return this.http.post<Success>(`${environment.apiUrl}subscriptions/unsubscribe`, {subscription_id});
  }

  // tslint:disable-next-line:variable-name
  public getFreeTrial(): Observable<Success> {
    return this.http.post<Success>(`${environment.apiUrl}subscriptions/free-trial`, {});
  }

  public getAdditionalGameSession(priceId: string): void {
    this.http.post<{ sessionId: string }>(`${environment.apiUrl}subscriptions/additional-game-format`, {priceId})
      .pipe(first())
      .subscribe((session: { sessionId: string }) => {
        this.redirectToCheckout(session);
      });
  }

  public buyAdditionalGameSession(quantity: string): Observable<Success> {
    // TODO remove if not used
    // return this.http.post<Success>(`${environment.apiUrl}subscriptions/buy-additional-game-format`, {});
    return this.http.post<Success>(`${environment.apiUrl}subscriptions/buy-events`, {quantity});
  }

  public buyAdditionalGame(body: AdditionalEventSub): Observable<{ sessionId: string }> {
    return this.http.post<{ sessionId: string }>(`${environment.apiUrl}subscriptions/event-session`, body);
  }

  public buyAdditionalRecurringGame(body: AdditionalEventSub): Observable<{ sessionId: string }> {
    return this.http.post<{ sessionId: string }>(`${environment.apiUrl}subscriptions/create-checkout-session`, body);
  }
}
